import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { describeExceptionProblem } from '../../healthcare-rates/healthcareErrors';
import { formatWireMoney } from '../../healthcare-rates/money';
import { SYNTHETIC_RATES_NOTICE, SYNTHETIC_RATES_NOTICE_TITLE, payBasisLabels, rateSourceLabels } from '../../healthcare-rates/types';
import { healthcareRatesGateway } from '../../healthcare-rates/index';
import { NotConnected } from './NotConnected';
import { TaxBreakdown } from './TaxBreakdown';
import type { PayrollRecord } from './usePayrollRecords';

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

interface Props {
  records: PayrollRecord[];
  /** Name filter typed into the header search box. */
  search: string;
  /** Show the leading checkbox column (Review / Pending Payroll). It has no backend yet, so it is rendered disabled. */
  showSelection: boolean;
  emptyMessage: string;
  /** Extra Action-column content per row, after View. */
  rowActions?: (record: PayrollRecord) => ReactNode;
  /** Clears the header search; offered when a search matches no row. */
  onResetSearch?: () => void;
}

const PAY_METHOD_LABELS: Record<string, string> = { Check: 'Check', DirectDeposit: 'Direct Deposit' };

/** The records table shared by Review, Pending and Approved Payroll: Employee, Discipline/Role, Pay Type, Earnings, Pay
 * (gross / net / check amount), Status and Action. Figures come from the saved calculation; nothing is recomputed. */
export function PayrollRecordsTable({ records, search, showSelection, emptyMessage, rowActions, onResetSearch }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? records.filter((r) => r.name.toLowerCase().includes(q)) : records;
  }, [records, search]);
  const columns = showSelection ? 9 : 8;

  return (
    <div className="cpr-records" tabIndex={0} role="region" aria-label="Payroll records">
      <table>
        <thead>
          <tr>
            {showSelection && (
              <th scope="col">
                <input type="checkbox" disabled aria-label="Select all employees (not yet connected)" title="Per-employee selection is not yet connected" />
              </th>
            )}
            <th scope="col">Employee</th>
            <th scope="col">Category/Role</th>
            <th scope="col">Pay Type</th>
            <th scope="col">Earnings</th>
            <th scope="col">Pay Method</th>
            <th scope="col">Pay</th>
            <th scope="col">Status</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr>
              <td colSpan={columns} className="muted">
                {records.length > 0 ? (
                  <>
                    No employee matches “{search.trim()}”.{' '}
                    {onResetSearch && (
                      <button type="button" className="link" onClick={onResetSearch}>
                        Reset search
                      </button>
                    )}
                  </>
                ) : (
                  emptyMessage
                )}
              </td>
            </tr>
          )}
          {visible.map((r) => (
            <Fragment key={r.employeeId}>
              <tr>
                {showSelection && (
                  <td>
                    <input type="checkbox" disabled aria-label={`Select ${r.name} (not yet connected)`} title="Per-employee selection is not yet connected" />
                  </td>
                )}
                <td>{r.name}</td>
                <td>
                  {r.role}
                  {r.disciplines.length > 0 && <div className="muted" data-testid="record-disciplines">Worker category: {r.disciplines.join(', ')}</div>}
                </td>
                <td>{r.payType || '—'}</td>
                <td>{r.earnings || '—'}</td>
                <td>
                  {r.line.payMethod !== undefined || r.payMethodBlocked ? (
                    <div className="cpr-paymethod">
                      <span>{r.line.payMethod ? (PAY_METHOD_LABELS[r.line.payMethod] ?? r.line.payMethod) : 'None'}</span>
                      {r.payMethodBlocked && (
                        <>
                          <span className="cpr-status cpr-status--bad">Not payable</span>
                          <a href={`/people/${r.employeeId}/employee-setup?section=payment`} target="_blank" rel="noopener noreferrer" aria-label={`Fix pay method for ${r.name} (opens the employee profile in a new tab)`}>
                            Fix in profile
                          </a>
                        </>
                      )}
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <div className="cpr-pay">
                    <span>
                      <strong>Gross Pay:</strong> {money(r.line.grossEarnings)}
                    </span>
                    <span>
                      <strong>Net Pay:</strong> {money(r.line.netPay)}
                    </span>
                    <span title="The calculation does not split pay by method yet">
                      <strong>Check Amount:</strong> —
                    </span>
                  </div>
                </td>
                <td>
                  <span className={`cpr-status ${r.canProcess ? 'cpr-status--ok' : 'cpr-status--bad'}`}>{r.canProcess ? 'Active' : "Can't Process"}</span>
                  {r.payMethodBlocked && <div className="muted">No usable pay method</div>}
                </td>
                <td>
                  <div className="cpr-actions">
                    <button
                      type="button"
                      className="cpr-btn cpr-btn--outline"
                      aria-expanded={openId === r.employeeId}
                      aria-label={`${openId === r.employeeId ? 'Hide' : 'View'} details for ${r.name}`}
                      onClick={() => setOpenId(openId === r.employeeId ? null : r.employeeId)}
                    >
                      {openId === r.employeeId ? 'Hide' : 'View'}
                    </button>
                    {rowActions?.(r)}
                  </div>
                </td>
              </tr>
              {openId === r.employeeId && (
                <tr className="cpr-row-detail">
                  <td colSpan={columns}>
                    {r.line.exceptionFlags && (() => {
                      const problem = describeExceptionProblem(r.line.exceptionFlags);
                      return problem ? (
                        <div className="hdc-problem" role="alert" data-testid="service-line-problem" data-code={problem.code}>
                          <p className="hdc-problem__title"><span aria-hidden="true">{'⚠'} </span>Can&apos;t process: {problem.title}</p>
                          {problem.detail && <p>{problem.detail}</p>}
                          <p><strong>How to fix it:</strong> {problem.fix}</p>
                        </div>
                      ) : (
                        <p className="cpr-notice cpr-notice--error" role="alert">
                          <strong>Can&apos;t process:</strong> {r.line.exceptionFlags}
                        </p>
                      );
                    })()}
                    {(r.line.earningDetails ?? []).length > 0 && (
                      <div className="cpr-earning-details" data-testid="earning-details">
                        <h4>How each earning was priced</h4>
                        {healthcareRatesGateway.isSynthetic && (
                          <p className="muted"><strong>{SYNTHETIC_RATES_NOTICE_TITLE}.</strong> {SYNTHETIC_RATES_NOTICE}</p>
                        )}
                        <table aria-label={`How each earning was priced for ${r.name}`}>
                          <thead>
                            <tr>
                              <th scope="col">Earning</th>
                              <th scope="col">Pay basis</th>
                              <th scope="col" className="num">Quantity</th>
                              <th scope="col" className="num">Rate</th>
                              <th scope="col">Rate source</th>
                              <th scope="col" className="num">Amount</th>
                              <th scope="col">How it was worked out</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(r.line.earningDetails ?? []).map((d, i) => (
                              <tr key={`${d.label}-${i}`}>
                                <td>{d.label}</td>
                                <td>{d.payBasis ? payBasisLabels[d.payBasis] : '—'}</td>
                                <td className="num">{d.quantity ?? '—'}</td>
                                <td className="num">{formatWireMoney(d.effectiveRate ?? d.rate)}</td>
                                <td>{d.rateSource ? rateSourceLabels[d.rateSource] : '—'}</td>
                                <td className="num">{money(d.amount)}</td>
                                <td>{d.explanation}{d.workedHours != null ? ` Worked hours kept: ${d.workedHours}.` : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {(r.line.warnings ?? []).length > 0 && (
                      <div className="cpr-notice cpr-notice--warning" role="status" data-testid="calculation-warnings">
                        <strong>Notes on this calculation</strong>
                        <ul>{(r.line.warnings ?? []).map((w) => <li key={w}>{w}</li>)}</ul>
                      </div>
                    )}
                    <TaxBreakdown employee={r.line} />
                    <p className="muted">
                      Per-paystub controls <NotConnected />
                    </p>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
